import React from 'react';
import { Link, useStaticQuery, graphql } from 'gatsby';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import { Icon } from '@components/icons';

const StyledSection = styled.section`
  .mobile-heading {
    margin-bottom: 16px;
    font-size: 14px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: var(--lightest-slate);

    @media (min-width: 1024px) {
      display: none;
    }
  }

  .list {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  .list:hover .item {
    opacity: 0.5;
  }

  .list .item:hover,
  .list .item:focus-within {
    opacity: 1;
  }

  .archive {
    display: inline-flex;
    align-items: center;
    margin-top: 16px;
    font-weight: 500;
    color: var(--lightest-slate);

    svg {
      width: 16px;
      height: 16px;
      margin-left: 4px;
      transition: transform 0.15s ease;
    }

    &:hover {
      color: var(--green);

      svg {
        transform: translateX(4px);
      }
    }
  }
`;

const ProjectCard = styled.li`
  position: relative;
  margin-bottom: 12px;
  padding-bottom: 4px;
  transition: opacity 0.15s ease;

  .glow {
    display: none;
    position: absolute;
    z-index: 0;
    border-radius: 8px;
    inset: -16px -24px;

    @media (min-width: 1024px) {
      display: block;
    }
  }

  &:hover .glow,
  &:focus-within .glow {
    background: rgba(30, 41, 59, 0.5);
    box-shadow: inset 0 1px 0 0 rgba(148, 163, 184, 0.1);
  }

  a.stretch {
    position: absolute;
    inset: 0;
    z-index: 1;

    span {
      ${({ theme }) => theme.mixins.visuallyHidden};
    }
  }

  .card {
    position: relative;
    z-index: 1;
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;

    @media (min-width: 640px) {
      grid-template-columns: repeat(8, 1fr);
      gap: 16px;
    }
  }

  .copy {
    @media (min-width: 640px) {
      grid-column: 3 / -1;
      grid-row: 1;
    }
  }

  .thumb {
    @media (min-width: 640px) {
      grid-column: 1 / 3;
      grid-row: 1;
    }

    .img,
    .gatsby-image-wrapper {
      width: 200px;
      max-width: 100%;
      border-radius: 6px;
      border: 2px solid rgba(226, 232, 240, 0.1);
      mix-blend-mode: normal;
      filter: none;
    }
  }

  h3 {
    font-size: 16px;
    font-weight: 500;
    color: var(--lightest-slate);
    margin: 0;
    line-height: 1.4;

    svg {
      display: inline-block;
      width: 16px;
      height: 16px;
      margin-left: 4px;
      vertical-align: -2px;
      transition: transform 0.15s ease;
    }
  }

  &:hover h3,
  &:focus-within h3 {
    color: var(--green);

    svg {
      transform: translate(2px, -2px);
    }
  }

  .body {
    position: relative;
    z-index: 2;
    margin-top: 8px;
    font-size: 14px;
    color: var(--slate);
  }

  .tech {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 12px;
    padding: 0;
    list-style: none;
  }

  .tech li {
    border-radius: 999px;
    background: var(--green-tint);
    color: var(--green);
    font-size: 12px;
    font-weight: 500;
    padding: 4px 12px;
  }
`;

const Featured = () => {
  const data = useStaticQuery(graphql`
    {
      featured: allMarkdownRemark(
        filter: { fileAbsolutePath: { regex: "/content/featured/" } }
        sort: { fields: [frontmatter___date], order: ASC }
      ) {
        edges {
          node {
            frontmatter {
              title
              cover {
                childImageSharp {
                  gatsbyImageData(width: 400, placeholder: BLURRED, formats: [AUTO, WEBP, AVIF])
                }
              }
              tech
              github
              external
            }
            html
          }
        }
      }
    }
  `);

  const featuredProjects = data.featured.edges.filter(({ node }) => node);

  return (
    <StyledSection id="projects" tabIndex="-1">
      <h2 className="mobile-heading">Projects</h2>
      <ul className="list">
        {featuredProjects.map(({ node }, i) => {
          const { frontmatter, html } = node;
          const { external, title, tech, github, cover } = frontmatter;
          const image = getImage(cover);
          const href = external || github || '#';

          return (
            <ProjectCard className="item" key={i}>
              <div className="glow" />
              <div className="card">
                <a className="stretch" href={href}>
                  <span>{title}</span>
                </a>
                <div className="copy">
                  <h3>
                    {title}
                    <Icon name="External" />
                  </h3>
                  <div className="body" dangerouslySetInnerHTML={{ __html: html }} />
                  {tech && (
                    <ul className="tech">
                      {tech.map(item => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  )}
                </div>
                {image && (
                  <div className="thumb">
                    <GatsbyImage image={image} alt="" className="img" />
                  </div>
                )}
              </div>
            </ProjectCard>
          );
        })}
      </ul>
      <Link className="archive" to="/archive">
        View Full Project Archive
        <Icon name="External" />
      </Link>
    </StyledSection>
  );
};

export default Featured;
